"use client";

import { Tooltip } from "react-tooltip";

import { Button } from "@/components/buttons/Button";
import { Badge } from "@/components/text/Badge";
import { defaultPitchShift } from "@/constants/defaultValues";
import { useConversionHyperParameters } from "@/lib/zustand/useConversionHyperParameters";
import { PitchRecommendation } from "@/types/defs/audioQuality/pitchRecommendation";
import { getPitchRecommendationStatus } from "@/utils/audio/quality/pitchWindow/getPitchRecommendationStatus";
import { isSinglePitch } from "@/utils/isSinglePitch";

export function PitchRecommendationAutoButton({
  pitchRecommendation,
}: {
  pitchRecommendation: PitchRecommendation;
}) {
  const pitch = useConversionHyperParameters((state) => state.pitch);
  const setPitch = useConversionHyperParameters((state) => state.setPitch);

  const currentPitchValue = isSinglePitch(pitch) ? pitch : pitch[0];
  const isRecommendedPitch =
    pitchRecommendation !== null &&
    currentPitchValue === pitchRecommendation.recommendedPitch;

  // Auto simply toggles between the recommended pitch and the default.
  const handleAutoPitchClick = () => {
    if (isRecommendedPitch) {
      setPitch(defaultPitchShift);
    } else if (pitchRecommendation !== null) {
      setPitch(pitchRecommendation.recommendedPitch);
    }
  };

  const pitchRecommendationStatus = getPitchRecommendationStatus(
    pitchRecommendation,
    currentPitchValue
  );

  if (pitchRecommendationStatus === null) {
    return null;
  }

  return (
    <div className="mb-4 flex items-center justify-between text-xs">
      <Badge
        dotColor={pitchRecommendationStatus.dotColor}
        className="bg-gray-100 px-2 py-1 text-[10px] font-semibold uppercase leading-4"
      >
        {pitchRecommendationStatus.label}
      </Badge>
      <Button
        variant={isRecommendedPitch ? "filled" : "default"}
        className="rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide"
        onClick={handleAutoPitchClick}
        data-tooltip-id="auto-pitch-tooltip"
      >
        Auto
      </Button>
      <Tooltip
        id="auto-pitch-tooltip"
        content={
          isRecommendedPitch ?
            "Reset to original pitch"
          : "Automatically sets pitch to the sweet spot for this voice"
        }
        place="top"
        className="z-10 !text-sm"
      />
    </div>
  );
}
